import { useState, useEffect, useCallback } from 'react';
import apiClient from '../utils/apiClient';
import { getCurrentUser } from '../utils/auth';

/**
 * Hook to fetch plan usage data from /billing/my-plan.
 *
 * Returns { usage, loading, error, refetch }
 *   usage: { students: { used, max }, batches: { used, max }, ... }
 *     max = 0 means unlimited.
 *
 * Only fetches for admin/superadmin roles. Tenant admins and students get empty usage.
 */
export function usePlanUsage() {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUsage = useCallback(async () => {
    try {
      const user = getCurrentUser();
      if (!user || !['admin', 'superadmin'].includes(user.usertype)) {
        setUsage(null);
        setLoading(false);
        return;
      }

      const { data } = await apiClient.get('/billing/my-plan');
      setUsage(data?.usage || null);
      setError(null);
    } catch (err) {
      console.warn('Failed to fetch plan usage:', err.message);
      setError(err.message);
      setUsage(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  return { usage, loading, error, refetch: fetchUsage };
}

/**
 * Check if a specific resource is over-limit.
 * @param {object} resourceUsage - { used, max } from the usage object
 * @returns {{ overLimit: boolean, used: number, max: number }}
 */
export function isOverLimit(resourceUsage) {
  if (!resourceUsage) return { overLimit: false, used: 0, max: 0 };
  const { used = 0, max = 0 } = resourceUsage;
  // max = 0 means unlimited
  if (max === 0) return { overLimit: false, used, max };
  return { overLimit: used >= max, used, max };
}

/**
 * Check if a specific resource is strictly over-limit (used > max, not equal).
 * Used for determining if edits should also be blocked.
 */
export function isStrictlyOverLimit(resourceUsage) {
  if (!resourceUsage) return { overLimit: false, used: 0, max: 0 };
  const { used = 0, max = 0 } = resourceUsage;
  if (max === 0) return { overLimit: false, used, max };
  return { overLimit: used > max, used, max };
}
